import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, User } from 'lucide-react';
import AdminUserManagement from '../../services/dashboard/AdminUserManagement';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

const DeleteUserModal = ({ isOpen, onClose, user, onUserDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Get admin roles from Redux
  const adminRoles = useSelector(state => state.adminAuth.roles || []);
  
  // Reset confirmText when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setConfirmText('');
    }
  }, [isOpen]);
  
  // Check if current admin has permission to delete
  // Handle both formats: "ROLE_SUPER_ADMIN" and "SUPER_ADMIN"
  const canDelete = adminRoles.some(role => 
    role === 'ROLE_SUPER_ADMIN' || 
    role === 'SUPER_ADMIN' || 
    role === 'ROLE_MODERATOR' || 
    role === 'MODERATOR'
  );

  const handleDelete = async () => {
    if (confirmText.toUpperCase() !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    try {
      console.log('Attempting to delete user:', user.userId);
      const response = await AdminUserManagement.deleteUser(user.userId);
      console.log('Delete response:', response);
      
      if (response.responseCode === 200) {
        toast.success('User deleted successfully!');
        onUserDeleted();
        handleClose();
      } else {
        toast.error(response.responseMessage || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.responseMessage || error.response?.data?.message || error.message || 'Failed to delete user';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  if (!isOpen || !user) return null;
  
  // If user doesn't have permission, show permission denied message
  if (!canDelete) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Permission Denied</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-700 dark:text-red-300">
                You do not have permission to delete users. Only Super Admin and Moderator roles can delete users.
              </p>
            </div>

            {/* Close Button */}
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete User</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                  Permanent Action
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                  This action cannot be undone. This will permanently delete the user account and remove all associated data.
                </p>
                <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                  <li>User profile and personal information</li>
                  <li>Order history and subscriptions</li>
                  <li>Wishlist and cart items</li>
                  <li>All user-generated content</li>
                </ul>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded-lg">
                <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user.emailId}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user.mobileNumber}</p>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type <span className="font-bold text-red-600">DELETE</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/50 dark:bg-gray-700 dark:text-white"
              placeholder="Type DELETE here"
              autoComplete="off"
            />
            {confirmText && confirmText.toUpperCase() !== 'DELETE' && (
              <p className="text-xs text-red-500 mt-1">Please type exactly: DELETE</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={handleClose}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || confirmText.toUpperCase() !== 'DELETE'}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  <span>Delete User</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserModal;